import { UiState } from "@/components/enums/UiState";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import CardBulletPoint from "@/components/shared/futureChat/cards/CardBulletPoint";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import { cn } from "@/lib/utils";
import { inject, observer } from "mobx-react";
import ExportedImage from "next-image-export-optimizer";
import { useContext, useEffect, useState } from "react";
import store from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";

type ImprintProps = {
    onClose: () => void;
    className?: string;
};

const Imprint: React.FC<ImprintProps> = ({ onClose, className }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { chatbotLocalized, globalLocalized, imprintLocalized } = store;
    const [windowWidth, setWindowWidth] = useState(0);

    const { screenView } = useContext(TrackingContext);

    useEffect(() => {
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        screenView('X.2 - Imprint')
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getImageSize = () => {
        if (windowWidth < 480) {
            return chatbotLocalized?.keyvisualDark320px;
        } else if (windowWidth < 768) {
            return chatbotLocalized?.keyvisualDark480px;
        } else if (windowWidth < 1024) {
            return chatbotLocalized?.keyvisualDark768px;
        } else if (windowWidth < 1280) {
            return chatbotLocalized?.keyvisualDark1024px;
        } else if (windowWidth < 1920) {
            return chatbotLocalized?.keyvisualDark1280px;
        } else if (windowWidth >= 1920) {
            return chatbotLocalized?.keyvisualDark1920px;
        }
    };

    const background = getImageSize();
    return (
        <div className="relative flex h-full w-full flex-col items-center">
            {background !== undefined && background !== null && (
                <div className={cn("absolute h-full w-full")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(background)}
                        alt={getAltTextFromGeneralImage(background)}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
            )}
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div className="relative h-[158px] w-[247px]">
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="contain"
                    />
                </div>
            )}
            <div
                className={cn(
                    "absolute bottom-[46px] flex w-full flex-col gap-[12px] px-[16px] text-[18px] max-w-[364px]",
                )}
            >
                <Card
                    className={cn(
                        "max-h-[317px] overflow-y-scroll bg-whiteShade-10",
                        "2xs:max-h-max 2xs:overflow-y-visible 2xs:px-[38px] 2xs:py-[28px]",
                    )}
                >
                    <div
                        className={cn(
                            "font-frutigerBlackItalic text-[18px] lg:text-[22px] font-black uppercase italic leading-[135%] tracking-minus-2 text-shadow-card-shared-text-stele",
                        )}
                    >
                        {imprintLocalized?.cardTitle}
                    </div>
                    <div
                        className={cn(
                            "font-frutigerRoman text-[13px] lg:text-[16px] leading-[135%] tracking-minus-2",
                        )}
                    >
                        {imprintLocalized?.cardSubtitle}
                    </div>
                    <div
                        className={cn(
                            "font-frutigerRoman text-[13px] lg:text-[16px] leading-[135%] tracking-minus-2",
                        )}
                    >
                        <div>{imprintLocalized?.cardStreet}</div>
                        <div>{imprintLocalized?.cardLocation}</div>
                        <div>{imprintLocalized?.cardPhoneNumber}</div>
                        <div>{imprintLocalized?.cardFaxNumber}</div>
                        <div>{imprintLocalized?.cardEMail}</div>
                    </div>
                    <div
                        className={cn(
                            "font-frutigerRoman text-[13px] lg:text-[16px] leading-[135%] tracking-minus-2",
                        )}
                    >
                        {imprintLocalized?.cardContent}
                    </div>
                </Card>
                <ButtonChat
                    iconRight={
                        <ExportedImage
                            src={
                                imprintLocalized?.button.icon !== undefined && imprintLocalized?.button.icon !== null
                                    ? getImageUrlFromGeneralImage(imprintLocalized.button.icon)
                                    : "No image provided"
                            }
                            alt={
                                imprintLocalized?.button.icon !== undefined && imprintLocalized?.button.icon !== null
                                    ? getAltTextFromGeneralImage(imprintLocalized.button.icon)
                                    : "No image provided"
                            }
                            layout="fill"
                            objectPosition="center"
                        />
                    }
                    variant="secondary"
                    onClick={onClose}
                >
                    {imprintLocalized?.button.text}
                </ButtonChat>
            </div>
        </div>
    );
};
export default inject("store")(observer(Imprint));
